import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { getActivePairings, resolvePairing } from '@features/booking/utils/pairings';
import type { SelectedPairing } from '@features/booking/types/pairings';

const MaridajePage = () => {
  const { t, i18n } = useTranslation('booking');
  const { goToNext } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();

  const pairings = useMemo(() => {
    return getActivePairings(restaurant.beveragePairings ?? []).map((p) =>
      resolvePairing(p, i18n.language),
    );
  }, [restaurant.beveragePairings, i18n.language]);

  // Resolve selected menu to check isPairingMandatory (legacy: selectedExperience.isPairingMandatory)
  // For WL flow: derive from wlSlotMenus (no selected slot exists).
  const isWlFlow = booking.flowType === 'waiting_list';
  let isPairingMandatory = false;
  if (isWlFlow) {
    const wlSlotMenus = booking.wlSlotMenus ?? [];
    isPairingMandatory = wlSlotMenus.length > 0 && !!wlSlotMenus[0].isPairingMandatory;
  } else {
    const selectedSlot = booking.selectedTime && booking.timeSlots
      ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
      : null;
    const selectedMenu = selectedSlot?.menus?.find(
      (m) => String(m.id) === booking.selectedExperience?.id,
    ) ?? selectedSlot?.menus?.[0];
    isPairingMandatory = !!selectedMenu?.isPairingMandatory;
  }

  // Initialize quantities from booking state or 0
  const [quantities, setQuantities] = useState<Record<number, number>>(() => {
    const init: Record<number, number> = {};
    for (const sp of booking.selectedPairings) {
      init[sp.id] = sp.quantity;
    }
    return init;
  });

  // Total quantity across all pairings (capped at partySize per legacy directive)
  const totalQuantity = useMemo(() => {
    return Object.values(quantities).reduce((sum, q) => sum + q, 0);
  }, [quantities]);

  const setQuantity = (id: number, qty: number) => {
    const currentQty = quantities[id] ?? 0;
    const delta = Math.max(0, qty) - currentQty;
    // Cap total at partySize (legacy: maridaje-select.js maximize check)
    if (delta > 0 && totalQuantity >= booking.diners) return;
    setQuantities((prev) => ({ ...prev, [id]: Math.max(0, qty) }));
  };

  // Mandatory validation per legacy: total must be >= partySize
  const canContinue = useMemo(() => {
    if (!isPairingMandatory) return true;
    return totalQuantity >= booking.diners;
  }, [isPairingMandatory, totalQuantity, booking.diners]);

  // Total cost
  const totalCost = useMemo(() => {
    let total = 0;
    for (const p of pairings) {
      const qty = quantities[p.id] ?? 0;
      if (qty > 0) total += p.price * qty;
    }
    return total;
  }, [pairings, quantities]);

  const currency = restaurant.currency || 'USD';

  const handleContinue = () => {
    const selected: SelectedPairing[] = [];
    for (const p of pairings) {
      const qty = quantities[p.id] ?? 0;
      if (qty > 0) {
        selected.push({
          id: p.id,
          name: p.name,
          price: p.price,
          priceUSD: p.priceUSD,
          quantity: qty,
          affectsGuarantee: p.affectsGuarantee,
        });
      }
    }
    booking.setSelectedPairings(selected);
    goToNext();
  };

  const handleSkip = () => {
    booking.setSelectedPairings([]);
    goToNext();
  };

  useEffect(() => {
    if (pairings.length === 0) goToNext();
  }, [pairings.length, goToNext]);

  if (pairings.length === 0) return null;

  return (
    <BookingPageLayout
      title={t('MARIDAJE_TITLE')}
      subtitle={t('MARIDAJE_SUBTITLE')}
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div className="w-full px-6 space-y-4">
        {pairings.map((pairing) => {
          const qty = quantities[pairing.id] ?? 0;

          return (
            <div
              key={pairing.id}
              className={`rounded-xl border p-4 transition-colors ${
                qty > 0 ? 'border-brand bg-brand/5' : 'border-border bg-card'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{pairing.name}</p>
                  {pairing.description && (
                    <p className="text-sm text-foreground/50 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pairing.description) }} />
                  )}
                  <p className="text-sm font-medium text-brand mt-1">
                    {currency} {pairing.price.toLocaleString()} {t('MARIDAJE_PER_PERSON')}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light active:scale-[0.96] transition disabled:opacity-50"
                    onClick={() => setQuantity(pairing.id, qty - 1)}
                    disabled={qty <= 0}
                  >
                    <Minus size={16} />
                  </button>
                  <div className="w-10 h-10 rounded-lg bg-foreground/5 border border-border flex items-center justify-center font-medium text-foreground text-sm tabular-nums">
                    {qty}
                  </div>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light active:scale-[0.96] transition disabled:opacity-50"
                    onClick={() => setQuantity(pairing.id, qty + 1)}
                    disabled={totalQuantity >= booking.diners}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {totalCost > 0 && (
          <div className="flex justify-between items-center pt-2 border-t border-border tabular-nums">
            <p className="text-sm text-foreground/70">{t('MARIDAJE_TOTAL')}</p>
            <p className="text-lg font-medium text-foreground">
              {currency} {totalCost.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {isPairingMandatory && !canContinue && (
        <p className="text-xs text-red-500 text-center px-6 mt-2">
          {totalQuantity === 0
            ? t('MARIDAJE_MANDATORY')
            : t('MARIDAJE_NEEDS_MORE', { pairingsSelected: totalQuantity })}
        </p>
      )}

      <div className="w-full px-6 mt-12 space-y-3">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {t('MARIDAJE_CONTINUE')}
        </Button>

        {!isPairingMandatory && (
          <Button
            variant="ghost"
            className="w-full text-foreground/50 hover:text-foreground/70"
            onClick={handleSkip}
          >
            {t('MARIDAJE_SKIP')}
          </Button>
        )}
      </div>
    </BookingPageLayout>
  );
};

export default MaridajePage;
